import { formatAddressLine } from './maps.ts';
import { legacyApple, mobileMapsBrowser, type RouteMapProvider } from './route-navigation.ts';
import type { AppointmentStatus } from './constants/index.ts';

/**
 * "Start my day": the day's visits in booked order, for whoever is doing them.
 *
 * No start or end point, no provider call and no reordering. Visit times are
 * fixed, so the booked order is the route, and the person's maps app already
 * knows where they are: every link here leaves the origin out, so navigation
 * starts from the phone's current location.
 */

export interface DayRouteVisit {
  id: string;
  customerName: string;
  propertyAddress: string | null;
  startsAt: string;
  endsAt: string;
  startTimeLocal: string;
  status: AppointmentStatus;
}

export type DayStopState = 'done' | 'missed' | 'next' | 'upcoming';

export interface DayStop<T extends DayRouteVisit = DayRouteVisit> {
  visit: T;
  /** 1-based position in the day, counting every stop that is still on it. */
  number: number;
  state: DayStopState;
  /** One address line, or '' when the visit has none to drive to. */
  address: string;
}

export interface DayRoute<T extends DayRouteVisit = DayRouteVisit> {
  stops: DayStop<T>[];
  next: DayStop<T> | null;
  /** Stops still to visit: the next one and everything after it. */
  remaining: number;
  done: number;
  /** Open visits whose end time has passed without being completed. */
  missed: number;
}

const OPEN: ReadonlySet<AppointmentStatus> = new Set(['scheduled', 'confirmed', 'change_requested']);

/**
 * Cancelled visits leave the day. A completed or no-show visit is done. The
 * next stop is the first open visit that has not ended yet; every open visit
 * whose end time has passed is shown as missed rather than holding the day up,
 * because a forgotten "complete" tap must not keep sending people backwards.
 */
export function buildDayRoute<T extends DayRouteVisit>(visits: readonly T[], now: number = Date.now()): DayRoute<T> {
  const ordered = visits
    .filter((visit) => visit.status !== 'cancelled')
    .slice()
    .sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt) || a.id.localeCompare(b.id));
  let next: DayStop<T> | null = null;
  const stops = ordered.map((visit, index): DayStop<T> => {
    const address = formatAddressLine(visit.propertyAddress);
    let state: DayStopState;
    // Ended before anything else: with overlapping bookings, a visit that
    // is already over must never count as a stop ahead.
    if (!OPEN.has(visit.status)) state = 'done';
    else if (Date.parse(visit.endsAt) <= now) state = 'missed';
    else if (next) state = 'upcoming';
    else state = 'next';
    const stop = { visit, number: index + 1, state, address };
    if (state === 'next') next = stop;
    return stop;
  });
  return {
    stops,
    next,
    remaining: stops.filter((stop) => stop.state === 'next' || stop.state === 'upcoming').length,
    done: stops.filter((stop) => stop.state === 'done').length,
    missed: stops.filter((stop) => stop.state === 'missed').length,
  };
}

/**
 * Turn-by-turn directions to one address from wherever the device is now, or
 * null when there is no address to drive to.
 */
export function directionsToAddress(address: string, provider: RouteMapProvider): string | null {
  const destination = address.trim();
  if (!destination) return null;
  if (provider === 'waze') return `https://waze.com/ul?${new URLSearchParams({ q: destination, navigate: 'yes' })}`;
  if (provider === 'apple')
    return `https://maps.apple.com/?${new URLSearchParams({ daddr: destination, dirflg: 'd' })}`;
  const query = new URLSearchParams({
    api: '1',
    destination,
    travelmode: 'driving',
    dir_action: 'navigate',
  });
  return `https://www.google.com/maps/dir/?${query}`;
}

/**
 * Every remaining stop in one link, from the device's current location, or
 * null when the maps app cannot take them all in one go. Never a link that
 * silently drops a stop, and never "part 1 of N": when the stops do not fit,
 * the caller offers the next stop instead.
 *
 * Google takes up to 3 waypoints in a mobile browser and 9 elsewhere. Apple's
 * multi-stop links need iOS 18.4 or later. Waze takes one destination.
 */
export function directionsToAll(
  addresses: readonly string[],
  provider: RouteMapProvider,
  options: { userAgent?: string; touchPoints?: number } = {},
): string | null {
  const stops = addresses.map((address) => address.trim());
  if (stops.length < 2 || stops.some((address) => !address)) return null;
  const destination = stops.at(-1)!;
  const waypoints = stops.slice(0, -1);
  const userAgent = options.userAgent ?? '';
  let url: string;
  if (provider === 'waze') return null;
  if (provider === 'apple') {
    if (legacyApple(userAgent) || waypoints.length > 13) return null;
    const query = new URLSearchParams({ destination, mode: 'driving' });
    waypoints.forEach((waypoint) => query.append('waypoint', waypoint));
    url = `https://maps.apple.com/directions?${query}`;
  } else {
    if (waypoints.length > (mobileMapsBrowser(userAgent, options.touchPoints) ? 3 : 9)) return null;
    const query = new URLSearchParams({
      api: '1',
      destination,
      waypoints: waypoints.join('|'),
      travelmode: 'driving',
      dir_action: 'navigate',
    });
    url = `https://www.google.com/maps/dir/?${query}`;
  }
  return url.length <= 2048 ? url : null;
}
